import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet"; // Import Helmet for meta tags
import {
  Grid,
  IconButton,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Card,
} from "@mui/material"; // Material UI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CrudService from "services/cruds-service"; // Assuming you have this service for API calls
import Footer from "examples/Footer";
import UserCard from "./userCard";
import LinkedIn from "@mui/icons-material/LinkedIn";
import Twitter from "@mui/icons-material/Twitter";
import LinkIcon from "@mui/icons-material/Link";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import quskillbridgelogo from "assets/images/quskillbridge-logo.png";

import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";

const CertificatePage = () => {
  // Get the course id and user id from the URL
  const location = useLocation();
  const pathSegments = location.pathname.split("/");

  const userId = pathSegments[2];
  const courseId = pathSegments[4];

  let link = location.pathname.split("https://qucoursify.s3.us-east-1.amazonaws.com")[1];
  link = "https://qucoursify.s3.us-east-1.amazonaws.com" + link;

  const [certificate, setCertificate] = useState(null); // Change to null for better handling
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailResponse, setDetailResponse] = useState({
    user_name: "",
    course_name: "",
    course_description: "",
    base64_data: "",
    course_modules_list: [],
  });

  // Fetch certificate on component mount
  useEffect(() => {
    const start_time = Date.now();
    async function fetchCertificate() {
      try {
        let response = await CrudService.getShowCertificate(courseId, userId, "explore"); // Pass both userId and courseId
        setDetailResponse(response);
        setCertificate(link); 
        setLoading(false);
      } catch (err) {
        console.log(err)
        setError("Failed to fetch certificate.");
        setLoading(false);
      }
    }

    fetchCertificate();
    analytics.track(events.VIEW_PAGE, { page: "view-certificate", userId: userId, courseId: courseId });
    return () => {
      analytics.track(events.PAGE_LEFT, { page: "view-certificate", userId: userId, courseId: courseId, start_time: start_time, end_time: Date.now() });
    }
  }, [userId, courseId]); // Dependency array includes userId and courseId

  // Generate LinkedIn Share URL
  let shareUrl = encodeURIComponent(window.location.href)

  const tweetText = `${detailResponse.user_name}, congratulations on completing ${detailResponse.course_name}!%0A%0ACheckout the certificate here:%0A`;
  const linkedInText = `${detailResponse.user_name}, congratulations on completing ${detailResponse.course_name}!%0A%0ACheckout the certificate here:%0A`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${linkedInText}&url=${shareUrl}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${shareUrl}`;

  if (loading) return <CircularProgress />;
  if (error) return <div>{error}</div>;

  const downloadImage = (certificate_image, course_name) => {
    const link = document.createElement("a");
    link.href = certificate_image;
    link.download = `Certificate-${course_name}.jpg`; // You can specify the filename here
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh", // Full viewport height
        width: "100vw", // Full viewport width
        border: 0,
        justifyContent: "space-between", // Space between elements
      }}
    >
      <Helmet>
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="QuSkillBridge - " />
        <meta property="og:description" content="Check out my certificate." />
        <meta property="og:image" content={quskillbridgelogo} />
        <meta property="og:url" content={window.location.href} />
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@yourTwitterHandle" />
        <meta name="twitter:title" content="Your Page Title" />
        <meta name="twitter:description" content="A description of your page" />
        <meta name="twitter:image" content={quskillbridgelogo} />
      </Helmet>

      <Grid container sx={{ justifyContent: "center" }}>
        {/* Header or Title */}
        <Grid container sx={{ border: 0, pt: 5 }}>
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <img src={quskillbridgelogo} alt="Quskillbridge Logo" width="15%" />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={3}
          sx={{
            border: 0,
            flexGrow: 1,
            px: 10,
            my: 1,
          }}
        >
          {/* Left Side: Image */}
          <Grid item xs={12} md={6} sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
            {certificate ? (
              <img
                src={certificate}
                alt={"Certificate"}
                id={courseId}
                style={{
                  maxWidth: "100%",       // Ensure the image does not stretch too wide

                  border: "10px solid #006caa",
                  borderImageSlice: 1,
                  borderImageSource: "linear-gradient(to right, #00d2ff, #3a7bd5)",
                  borderRadius: "10px",
                }}
              />
            ) : (
              <Typography variant="subtitle1" color="textSecondary">
                No certificate available.
              </Typography>
            )}
          </Grid>

          {/* Right Side: User Card, Heading, Text, Title, Buttons */}
          <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Card
              style={{
                flexGrow: 1,
                padding: "20px",
                boxShadow: "none",
              }}
            >
              <MDTypography variant="h3">
                {detailResponse.course_name}
              </MDTypography>

              <MDTypography variant="body1">
                {detailResponse.course_description}
              </MDTypography>

              <MDTypography variant="h4" my={1}>
                Course Preview
              </MDTypography>

              <MDBox
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height:
                    detailResponse.course_modules_list.length > 4
                      ? "30vh"
                      : "auto",
                  flexGrow: 1,
                  overflow: "auto",
                }}
              >
                {detailResponse.course_modules_list.map((module, index) => (
                  <MDBox
                    key={index}
                    p={1}
                    display="flex"
                    style={{
                      borderLeft: "10px solid gray",
                      border: "1px solid #e0e0e0",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    <MDBox
                      display="flex"
                      style={{
                        width: "100%",
                        flexDirection: "column",
                      }}
                    >
                      <MDTypography variant="body1">
                        {index + 1}. {module}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ border: 0, flexGrow: 1, py: 1, borderColor: "red", my: 1 }}
        >
          {/* LinkedIn and Twitter Icons */}
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            lg={4}
            xl={3}
            sx={{ mx: "auto", border: 0 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              color="info"
              onClick={() => window.open(linkedInShareUrl, "_blank")}
              aria-label="Share on LinkedIn"
            >
              <LinkedIn fontSize="large" />
            </IconButton>
            <IconButton
              color="info"
              onClick={() => window.open(twitterShareUrl, "_blank")}
              aria-label="Share on Twitter"
            >
              <Twitter fontSize="large" />
            </IconButton>
            <IconButton
              color="info"
              onClick={() => {
                const certificateUrl = window.location.href;
                navigator.clipboard.writeText(certificateUrl)
                  .then(() => alert("Certificate link copied to clipboard!"))
                  .catch(err => console.error("Failed to copy: ", err));
              }}
              aria-label="Copy Certificate Link"
            >
              <LinkIcon fontSize="large" />
            </IconButton>
            <IconButton
              onClick={() => downloadImage(certificate, detailResponse.course_name)}
              color="info"
              aria-label="Download Certificate"
            >
              <FileDownloadIcon fontSize="large" />
            </IconButton>
          </Grid>

          {/* Go to Course Button */}
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            lg={4}
            xl={3}
            sx={{ mx: "auto", border: 0 }}
          >
            <MDButton
              variant="contained"
              color="info"
              onClick={() =>
                window.open(`/course-information/${courseId}`, "_blank")
              }
              sx={{ width: "100%", border: 0 }}
            >
              Course Details
            </MDButton>
          </Grid>
        </Grid>

        {/* Footer at the bottom */}
      </Grid>
      <Footer />
    </MDBox>
  );
};

export default CertificatePage;
